/**
 * useScaleListEditDialog — Track 3 Phase 4
 *
 * Owns the SCALELISTEDIT dialog feature (PR-9) extracted from
 * `App.tsx` as part of the Track 3 App.tsx decomposition (see
 * `docs/plans/2026-05-08_track3_app_decomposition.md`).
 *
 * Responsibilities:
 *   - Owns the `scaleListEditDialogOpen` flag and exposes imperative
 *     `open()` / `close()` for the command-line router (SCALELISTEDIT /
 *     СПИСМАСШТАБОВ), the AnnotationStatusBar SCALELISTEDIT button, and
 *     `resetShellState()`.
 *   - The command-line entry path also dispatches
 *     `system/open-scalelist-dialog` (audit-only) — that side-effect
 *     stays at the call site so the audit precedes the open.
 *   - Owns the Add / Remove / Reset handlers — Add and Remove dispatch
 *     `header/scalelist-add` and `header/scalelist-remove` directly;
 *     Reset emits a series of removes followed by a series of adds
 *     (single-undo per action — matches AutoCAD's reset-as-batch).
 *
 * Returned shape:
 *   {
 *     isOpen,
 *     props: { open, scales, onAdd, onRemove, onReset, onClose },
 *     open,
 *     close,
 *   }
 *
 * The orchestrator spreads `props` onto `<ScaleListEditDialog>` (note:
 * the dialog itself accepts an `open` prop and is *always mounted* —
 * it gates internally — so `props.open` mirrors `isOpen`).
 *
 * AutoCAD parity:
 *   AutoCAD's SCALELISTEDIT command opens the "Edit Scale List" dialog
 *   that manages the document-level annotation scale catalogue. The
 *   dialog component preserves the full surface; the hook only owns
 *   orchestration and store binding.
 */

import { useCallback } from "react";

import { useEditorStore } from "../state/editorStore";
import { useDialogOpenState } from "./useDialogOpenState";

// Phase 1.k (П35Р React #185 fix) — stable sentinel empty array. `?? []`
// inside a Zustand selector allocates a fresh array on every store
// update, defeating Object.is bail-out → spurious re-renders.
const EMPTY_SCALES: NonNullable<
  ReturnType<typeof useEditorStore.getState>["geometryDocument"]["annotationScales"]
> = [];

export interface UseScaleListEditDialogResult {
  /** Whether the dialog is open. Mirrored into `props.open`. */
  isOpen: boolean;
  /** Props bundle for `<ScaleListEditDialog>` (always mounted). */
  props: {
    open: boolean;
    scales: ReadonlyArray<{
      name: string;
      scaleFactor: number;
      isHidden?: boolean;
    }>;
    onAdd: (name: string, factor: number) => void;
    onRemove: (name: string) => void;
    onReset: (
      next: ReadonlyArray<{ name: string; scaleFactor: number }>,
    ) => void;
    onClose: () => void;
  };
  /**
   * Imperative open — used by the command-line router (SCALELISTEDIT /
   * СПИСМАСШТАБОВ) and the AnnotationStatusBar SCALELISTEDIT button.
   */
  open: () => void;
  /** Imperative close — used by `resetShellState()`. */
  close: () => void;
}

export function useScaleListEditDialog(): UseScaleListEditDialogResult {
  const dispatch = useEditorStore((store) => store.dispatch);
  const annotationScales = useEditorStore(
    (store) => store.geometryDocument.annotationScales ?? EMPTY_SCALES,
  );

  const { isOpen, open, close } = useDialogOpenState();

  const onAdd = useCallback(
    (name: string, factor: number) => {
      dispatch({
        type: "header/scalelist-add",
        source: "scene",
        scaleName: name,
        scaleFactor: factor,
        isHidden: false,
      });
    },
    [dispatch],
  );

  const onRemove = useCallback(
    (name: string) => {
      dispatch({
        type: "header/scalelist-remove",
        source: "scene",
        scaleName: name,
      });
    },
    [dispatch],
  );

  const onReset = useCallback(
    (next: ReadonlyArray<{ name: string; scaleFactor: number }>) => {
      // Reset = remove all current entries + add defaults. Each
      // dispatch is its own undo step (acceptable parity — AutoCAD
      // also records the reset as a sequence of mutations).
      for (const existing of annotationScales) {
        dispatch({
          type: "header/scalelist-remove",
          source: "scene",
          scaleName: existing.name,
        });
      }
      for (const fresh of next) {
        dispatch({
          type: "header/scalelist-add",
          source: "scene",
          scaleName: fresh.name,
          scaleFactor: fresh.scaleFactor,
          isHidden: false,
        });
      }
    },
    [dispatch, annotationScales],
  );

  return {
    isOpen,
    props: {
      open: isOpen,
      scales: annotationScales,
      onAdd,
      onRemove,
      onReset,
      onClose: close,
    },
    open,
    close,
  };
}
